import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import styles from './RnRPage.module.css';

// From the R&R Policy (effective 01 Jul 2026) — Award Name is what nominators pick from.
const AWARD_CATEGORIES = [
  { name: 'Density Star', category: 'Spot Award', amount: '₹2,500', frequency: 'Quarterly' },
  { name: 'Density Inspire', category: 'Inspire to Lead Award', amount: '₹3,000', frequency: 'Quarterly' },
  { name: 'Density Rising Star', category: 'Emerging Talent – New Talent', amount: '₹2,500 / ₹5,000', frequency: 'Quarterly / Annual' },
  { name: 'One Density Award', category: 'Team Excellence Award', amount: '₹1,500 each', frequency: 'Quarterly / Annual' },
  { name: 'Density Extra Mile Award', category: 'Above & Beyond Award', amount: '₹5,000', frequency: 'Quarterly / Annual' },
  { name: 'Density Quality Champion', category: 'Quality Champion', amount: '₹3,000', frequency: 'Quarterly / Annual' },
  { name: 'Customer Delight Champion', category: 'Customer Delight Award', amount: '₹3,000', frequency: 'Quarterly / Annual' },
  { name: 'Employee of the Quarter', category: 'Exceptional Performance', amount: '₹5,000', frequency: 'Quarterly' },
  { name: 'Employee of the Year', category: 'Highest Annual Recognition', amount: '₹10,000', frequency: 'Annual' },
  { name: 'Density Excellence', category: 'Exceptional Performance', amount: '₹10,000', frequency: 'Annual' },
  { name: 'Leadership Excellence Award', category: 'Leader of the Year', amount: '₹12,000', frequency: 'Annual' },
  { name: 'Long Service & Commitment Award', category: 'Long Service Award', amount: '₹3,000 / ₹5,000 / ₹10,000', frequency: 'Annual' },
  { name: 'Management Award', category: 'Management Award', amount: 'As per Management', frequency: 'Annual' },
];

// Which categories are offered depends on the window's frequency — "Quarterly / Annual"
// categories are valid either way.
const categoriesFor = (frequency) => {
  const tag = frequency === 'QUARTERLY' ? 'Quarterly' : 'Annual';
  return AWARD_CATEGORIES.filter((a) => a.frequency.includes(tag));
};

const MAX_NOMINATIONS_PER_NOMINATOR = 2;

const EMPTY_WINDOW_FORM = { label: '', frequency: '', startDate: '', endDate: '' };
const EMPTY_NOMINATION_FORM = { nominatedEmployeeId: '', awardCategory: '', reason: '', supportingEvidence: '' };

const RnRPage = () => {
  const { user } = useAuth();
  const isHR = !!user?.isHR;

  const [windows, setWindows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [myNomsByWindow, setMyNomsByWindow] = useState({});
  const [allNomsByWindow, setAllNomsByWindow] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedWindowId, setExpandedWindowId] = useState(null);

  const [nomForms, setNomForms] = useState({}); // { [windowId]: form }
  const [nomErrors, setNomErrors] = useState({}); // { [windowId]: message }
  const [nomSuccess, setNomSuccess] = useState({}); // { [windowId]: message }
  const [submittingWindowId, setSubmittingWindowId] = useState(null);

  const [windowForm, setWindowForm] = useState(EMPTY_WINDOW_FORM);
  const [windowError, setWindowError] = useState('');
  const [windowSaving, setWindowSaving] = useState(false);
  const [showCreateWindow, setShowCreateWindow] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [windowsRes, employeesRes] = await Promise.all([
        api.getRnrWindows(),
        api.getAllEmployees(),
      ]);
      const windowList = windowsRes.success ? windowsRes.data : []; // newest-created first
      setWindows(windowList);
      if (employeesRes.success) setEmployees(employeesRes.data);

      const mineMap = {}, allMap = {};
      await Promise.all(windowList.map(async (w) => {
        const mineRes = await api.getMyRnrNominations(w.id);
        if (mineRes.success) mineMap[w.id] = mineRes.data;
        if (isHR) {
          const allRes = await api.getAllRnrNominations(w.id);
          if (allRes.success) allMap[w.id] = allRes.data;
        }
      }));
      setMyNomsByWindow(mineMap);
      setAllNomsByWindow(allMap);

      // Default to the newest window expanded, first load only.
      setExpandedWindowId((prev) => (prev !== null ? prev : (windowList[0]?.id ?? null)));
    } catch { /* ignore — panels just render empty */ }
    finally { setLoading(false); }
  }, [isHR]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const setFormFor = (windowId, patch) =>
    setNomForms((p) => ({ ...p, [windowId]: { ...(p[windowId] || EMPTY_NOMINATION_FORM), ...patch } }));

  const handleNominate = async (e, windowId) => {
    e.preventDefault();
    const form = nomForms[windowId] || EMPTY_NOMINATION_FORM;
    setNomErrors((p) => ({ ...p, [windowId]: '' }));
    setNomSuccess((p) => ({ ...p, [windowId]: '' }));
    if (!form.nominatedEmployeeId || !form.awardCategory || !form.reason.trim()) {
      setNomErrors((p) => ({ ...p, [windowId]: 'Please pick a nominee, an award, and describe why.' }));
      return;
    }
    setSubmittingWindowId(windowId);
    try {
      const res = await api.submitRnrNomination({
        windowId,
        nominatedEmployeeId: Number(form.nominatedEmployeeId),
        awardCategory: form.awardCategory,
        reason: form.reason,
        supportingEvidence: form.supportingEvidence,
      });
      if (res.success) {
        setNomSuccess((p) => ({ ...p, [windowId]: 'Nomination submitted!' }));
        setNomForms((p) => ({ ...p, [windowId]: EMPTY_NOMINATION_FORM }));
        loadAll();
      } else {
        setNomErrors((p) => ({ ...p, [windowId]: res.message }));
      }
    } catch {
      setNomErrors((p) => ({ ...p, [windowId]: 'Something went wrong. Please try again.' }));
    } finally {
      setSubmittingWindowId(null);
    }
  };

  const handleCreateWindow = async (e) => {
    e.preventDefault();
    setWindowError('');
    if (!windowForm.label || !windowForm.frequency || !windowForm.startDate || !windowForm.endDate) {
      setWindowError('Label, frequency, start date and end date are all required.');
      return;
    }
    setWindowSaving(true);
    try {
      const res = await api.createRnrWindow(windowForm);
      if (res.success) {
        setWindowForm(EMPTY_WINDOW_FORM);
        setShowCreateWindow(false);
        setExpandedWindowId(res.data.id);
        loadAll();
      } else {
        setWindowError(res.message);
      }
    } catch { setWindowError('Failed to create window.'); }
    finally { setWindowSaving(false); }
  };

  const toggleWindowOpen = async (windowId, open) => {
    const res = await api.setRnrWindowOpen(windowId, open);
    if (res.success) loadAll();
    else alert(res.message);
  };

  if (loading) {
    return <div className={styles.container}><div className={styles.loading}>Loading...</div></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>🏆 Rewards & Recognition</div>
        <div className={styles.pageSubtitle}>Nominate a peer or team member for an eligible award.</div>
      </div>

      {isHR && (
        <div className={styles.card}>
          {!showCreateWindow ? (
            <button className={styles.addWindowBtn} onClick={() => setShowCreateWindow(true)}>
              + New Nomination Window
            </button>
          ) : (
            <form onSubmit={handleCreateWindow} className={styles.formGrid}>
              {windowError && <div className={`${styles.formError} ${styles.spanFull}`}>{windowError}</div>}
              <div className={styles.formGroup}>
                <label className={styles.label}>Label</label>
                <input className={styles.input} placeholder="e.g. Q4 2026"
                  value={windowForm.label}
                  onChange={(e) => setWindowForm((p) => ({ ...p, label: e.target.value }))} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Frequency</label>
                <select className={styles.select}
                  value={windowForm.frequency}
                  onChange={(e) => setWindowForm((p) => ({ ...p, frequency: e.target.value }))}>
                  <option value="">Select frequency</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="ANNUAL">Annual</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Start Date</label>
                <input className={styles.input} type="date"
                  value={windowForm.startDate}
                  onChange={(e) => setWindowForm((p) => ({ ...p, startDate: e.target.value }))} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>End Date</label>
                <input className={styles.input} type="date"
                  value={windowForm.endDate}
                  onChange={(e) => setWindowForm((p) => ({ ...p, endDate: e.target.value }))} />
              </div>
              <div className={styles.spanFull} style={{ display: 'flex', gap: 10 }}>
                <button className={styles.submitBtn} type="submit" disabled={windowSaving}>
                  {windowSaving ? 'Creating...' : 'Create Window'}
                </button>
                <button className={styles.cancelBtn} type="button" onClick={() => setShowCreateWindow(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {windows.length === 0 ? (
        <div className={styles.card}>
          <div className={styles.emptyState}>
            No nomination windows have been created yet.{isHR ? ' Create one above to get started.' : ''}
          </div>
        </div>
      ) : (
        windows.map((w) => (
          <WindowPanel
            key={w.id}
            window={w}
            isExpanded={expandedWindowId === w.id}
            onToggle={() => setExpandedWindowId(expandedWindowId === w.id ? null : w.id)}
            isHR={isHR}
            employees={employees}
            currentUserEmail={user?.email}
            myNominations={myNomsByWindow[w.id] || []}
            allNominations={allNomsByWindow[w.id] || []}
            form={nomForms[w.id] || EMPTY_NOMINATION_FORM}
            setForm={(patch) => setFormFor(w.id, patch)}
            error={nomErrors[w.id] || ''}
            success={nomSuccess[w.id] || ''}
            submitting={submittingWindowId === w.id}
            onNominate={(e) => handleNominate(e, w.id)}
            onToggleOpen={(open) => toggleWindowOpen(w.id, open)}
          />
        ))
      )}

      {/* Award categories reference */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>Award Categories</div>
        <div className={styles.tableScroll}>
          <table className={styles.refTable}>
            <thead>
              <tr>
                <th>Award Name</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Frequency</th>
              </tr>
            </thead>
            <tbody>
              {AWARD_CATEGORIES.map((a) => (
                <tr key={a.name}>
                  <td>{a.name}</td>
                  <td>{a.category}</td>
                  <td>{a.amount}</td>
                  <td>{a.frequency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// One collapsible panel per nomination window — lets an employee (or HR) switch between
// several simultaneously-open windows instead of only ever seeing "the" latest one.
// Winner selection happens outside the app (HR reviews everything in Excel), so nominations
// here are just submitted and listed — no status/action tracking.
const WindowPanel = ({
  window: w, isExpanded, onToggle, isHR, employees, currentUserEmail,
  myNominations, allNominations,
  form, setForm, error, success, submitting, onNominate, onToggleOpen,
}) => {
  const hasReachedLimit = myNominations.length >= MAX_NOMINATIONS_PER_NOMINATOR;
  const nomineeOptions = employees
    .filter((emp) => emp.isActive && emp.email?.toLowerCase() !== currentUserEmail?.toLowerCase())
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className={`${styles.windowPanel} ${w.isOpen ? styles.panelOpen : ''}`}>
      <button type="button" className={styles.windowPanelHeader} onClick={onToggle}>
        <div className={styles.windowPanelHeaderLeft}>
          <strong>{w.label}</strong>
          <span className={styles.windowDates}>{w.startDate} – {w.endDate}</span>
          <span className={`${styles.windowStatusTag} ${styles.tagFrequency}`}>
            {w.frequency === 'QUARTERLY' ? 'Quarterly' : 'Annual'}
          </span>
          <span className={`${styles.windowStatusTag} ${w.isOpen ? styles.tagOpen : styles.tagClosed}`}>
            {w.isOpen ? 'Open' : 'Closed'}
          </span>
        </div>
        <span className={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className={styles.windowPanelBody}>
          {isHR && (
            <div className={styles.hrWindowActions}>
              <button
                className={w.isOpen ? styles.closeBtn : styles.openBtn}
                onClick={() => onToggleOpen(!w.isOpen)}>
                {w.isOpen ? 'Close this window' : 'Open this window'}
              </button>
            </div>
          )}

          {myNominations.length > 0 && (
            <div className={styles.card}>
              <div className={styles.cardTitle}>Your Nomination{myNominations.length > 1 ? 's' : ''}</div>
              <div className={styles.nomList}>
                {myNominations.map((n) => (
                  <div key={n.id} className={styles.nomRow}>
                    <div>
                      <strong>{n.nominatedEmployeeName}</strong> — {n.awardCategory}
                      <div className={styles.nomReason}>{n.reason}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasReachedLimit ? (
            <div className={styles.card}>
              <div className={styles.cardTitle}>📝 Nominate a Colleague</div>
              <div className={styles.closedNote}>
                You've submitted the maximum of {MAX_NOMINATIONS_PER_NOMINATOR} nominations for this window.
              </div>
            </div>
          ) : (
            <div className={styles.card}>
              <div className={styles.cardTitle}>📝 Nominate a Colleague</div>
              {!w.isOpen && (
                <div className={styles.closedNote}>
                  This window is closed — HR isn't accepting new nominations here right now.
                </div>
              )}
              {error && <div className={styles.formError}>{error}</div>}
              {success && <div className={styles.formSuccess}>{success}</div>}
              <form onSubmit={onNominate} className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Nominee</label>
                  <select className={styles.select}
                    value={form.nominatedEmployeeId}
                    onChange={(e) => setForm({ nominatedEmployeeId: e.target.value })}
                    disabled={!w.isOpen}>
                    <option value="">Select an employee</option>
                    {nomineeOptions.map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.name} — {emp.department}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Award - Category</label>
                  <select className={styles.select}
                    value={form.awardCategory}
                    onChange={(e) => setForm({ awardCategory: e.target.value })}
                    disabled={!w.isOpen}>
                    <option value="">Select an award</option>
                    {categoriesFor(w.frequency).map((a) => (
                      <option key={a.name} value={a.name}>{a.name} - {a.category}</option>
                    ))}
                  </select>
                </div>
                <div className={`${styles.formGroup} ${styles.spanFull}`}>
                  <label className={styles.label}>Reason for Nomination</label>
                  <textarea className={styles.textarea} rows={3}
                    placeholder="Describe the achievement, behaviour, or contribution and how it aligns with our values."
                    value={form.reason}
                    onChange={(e) => setForm({ reason: e.target.value })}
                    disabled={!w.isOpen} />
                </div>
                <div className={`${styles.formGroup} ${styles.spanFull}`}>
                  <label className={styles.label}>Supporting Evidence (optional)</label>
                  <textarea className={styles.textarea} rows={2}
                    placeholder="Customer appreciation, project outcomes, measurable results, etc."
                    value={form.supportingEvidence}
                    onChange={(e) => setForm({ supportingEvidence: e.target.value })}
                    disabled={!w.isOpen} />
                </div>
                <div className={styles.spanFull}>
                  <button className={styles.submitBtn} type="submit" disabled={!w.isOpen || submitting}>
                    {submitting ? 'Submitting...' : 'Submit Nomination'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {isHR && (
            <div className={styles.card}>
              <div className={styles.cardTitle}>All Nominations — {w.label}</div>
              {allNominations.length === 0 ? (
                <div className={styles.emptyState}>No nominations yet for this window.</div>
              ) : (
                <div className={styles.tableScroll}>
                  <table className={styles.refTable}>
                    <thead>
                      <tr>
                        <th>Nominee</th>
                        <th>Dept</th>
                        <th>Category</th>
                        <th>Reason</th>
                        <th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allNominations.map((n) => (
                        <tr key={n.id}>
                          <td>{n.nominatedEmployeeName}</td>
                          <td>{n.department}</td>
                          <td>{n.awardCategory}</td>
                          <td style={{ maxWidth: 260, whiteSpace: 'normal', fontSize: 12 }}>{n.reason}</td>
                          <td>{n.nominatedByName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RnRPage;
